import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useSpeakerProfile, useSpeakerProfiles, useSubmitSpeakerCommand } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClipAudio } from 'src/components/editorial/ClipAudio';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { PendingCommands } from 'src/components/editorial/PendingCommands';
import { UploadClips } from 'src/components/editorial/UploadClips';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Skeleton } from 'src/components/ui/Skeleton';
import {
  PROFILE_STATUS_LABEL,
  PROFILE_STATUS_MEANING,
  PROFILE_STATUS_TONE,
  formatClipOffset,
  profileStrength,
} from 'src/lib/speakerProfiles';
import { formatDateTime } from 'src/lib/time';
import type { EditorialSpeakerClip, EditorialSpeakerProfile, SuggestedName } from 'src/types/editorial';

/**
 * One voice: who it is, what audio taught it, and what to do about it.
 *
 * The clip list is the point. Before this existed a profile was a single averaged vector with no
 * way to see what went into it — a mislabeled segment was permanent and invisible. Here every clip
 * is playable next to the line that was spoken in it, and excluding one recomputes the voice
 * fingerprint from what is left. Nothing is destroyed: an exclusion is reversible.
 */
export function VoiceProfileWorkspace() {
  const { profileId = '' } = useParams<{ profileId: string }>();
  const profile = useSpeakerProfile(profileId);

  if (profile.isPending) {
    return (
      <div className="page-wide">
        <Skeleton variant="rows" count={8} />
      </div>
    );
  }

  if (profile.isError) {
    return (
      <div className="page-wide">
        <LoadFailure title="No se pudo cargar la voz" error={profile.error} onRetry={profile.refetch}
          whenMissing="El panel de voces necesita una versión del API que quizá aún no está desplegada. Si acabas de actualizar el dashboard, despliega envoz-server."
        />
      </div>
    );
  }

  const { profile: voice, clips, commands, suggestedNames = [] } = profile.data;
  const strength = profileStrength(voice);

  return (
    <div className="page-wide">
      <nav className="voice-detail__back">
        <Link to="/voces">← Todas las voces</Link>
      </nav>

      <header className="voice-detail__head landmark">
        <div>
          <h1 className="section-title">{voice.name}</h1>
          <Badge tone={PROFILE_STATUS_TONE[voice.status]}>{PROFILE_STATUS_LABEL[voice.status]}</Badge>
          <p className="relevance__note">{PROFILE_STATUS_MEANING[voice.status]}</p>
        </div>
      </header>

      <dl className="voice-row__facts">
        <div>
          <dt>Clips que forman la huella</dt>
          <dd>{voice.clipCount}</dd>
        </div>
        <div>
          <dt>Clips excluidos</dt>
          <dd>{voice.excludedClipCount}</dd>
        </div>
        <div>
          <dt>Segmentos reconocidos</dt>
          <dd>{voice.matchCount}</dd>
        </div>
        <div>
          <dt>Creada</dt>
          <dd>{formatDateTime(voice.createdAt)}</dd>
        </div>
      </dl>

      <p className="voice-row__strength" data-level={strength.level}>
        <strong>{strength.label}.</strong> {strength.advice}
      </p>

      <PendingCommands commands={commands} />

      {/* The screen's question is "who is this", so naming comes first and the clips — the evidence
          for the answer — come directly under it. The upload form used to open the page and the
          clips were below the fold, which put the tool above the job. */}
      <NameThisVoice profile={voice} suggestions={suggestedNames} />

      <section className="voice-detail__clips">
        <h2 className="section-title">Audios de entrenamiento</h2>
        <p className="relevance__note">
          Cada clip es un fragmento real del stream. Escúchalos para confirmar que todos son la misma persona: un clip
          que no corresponda se excluye y la huella se recalcula sin él. Excluir no borra nada — se puede volver a
          incluir y la voz queda exactamente como estaba.
        </p>

        {clips.length === 0 ? (
          <AbsenceState
            kind="empty"
            what="Esta voz no tiene clips guardados."
            why="Sin clips no hay huella y no puede reconocer a nadie."
            wouldChange="Se llenará sola cuando la voz vuelva a aparecer en un stream."
          />
        ) : (
          <ul className="clips">
            {clips.map(clip => (
              <li key={clip.clipId}>
                <ClipRow clip={clip} />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* The tail: everything that is a tool rather than the decision. */}
      <div className="quiet-tail">
        <UploadClips profileId={voice.profileId} />
        <ProfileActions profile={voice} />
      </div>
    </div>
  );
}

/**
 * Naming the voice, with what the transcripts already called it.
 *
 * The pipeline names speakers twice and the halves never met: the voice matcher groups segments
 * that sound alike into `Voz 3`, and a later stage reads the transcript and writes "Claudia
 * Sheinbaum" onto the turn. The second never reached the first, so a transcript could read
 * perfectly while every profile in the panel sat unnamed.
 *
 * The suggestion is offered, never applied. A name taken from a model's inference would follow
 * this voice into every future transcript, and being wrong there is worse than being unnamed —
 * so it arrives with its evidence, and a person presses the button.
 */
function NameThisVoice({ profile, suggestions }: { profile: EditorialSpeakerProfile; suggestions: SuggestedName[] }) {
  const submit = useSubmitSpeakerCommand();
  const [name, setName] = useState(profile.name);

  return (
    <section className="name-voice">
      <h2 className="section-title">¿Quién es?</h2>

      {suggestions.length > 0 && (
        <p className="name-voice__suggested">
          En las transcripciones, a esta voz se le llamó:{' '}
          {suggestions.map(suggestion => (
            <button
              key={suggestion.name}
              type="button"
              className="name-voice__pick"
              onClick={() => setName(suggestion.name)}
            >
              {suggestion.name} <span className="name-voice__count">{suggestion.clips}</span>
            </button>
          ))}
          . Confírmalo escuchando algún clip; el nombre lo dedujo un modelo, no el audio.
        </p>
      )}

      <form
        className="name-voice__form"
        onSubmit={event => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed.length >= 2) submit.mutate({ profileId: profile.profileId, type: 'rename_profile', payload: { name: trimmed } });
        }}
      >
        <label className="name-voice__field">
          <span>Nombre de esta voz</span>
          <input type="text" value={name} onChange={event => setName(event.target.value)} minLength={2} required />
        </label>
        <Button type="submit" variant="primary" busy={submit.isPending} disabled={name.trim().length < 2 || name.trim() === profile.name}>
          Guardar nombre
        </Button>
      </form>
      <p className="name-voice__note">
        Ponerle nombre la marca como identificada. A partir de la siguiente corrida, sus segmentos aparecen con este
        nombre en las transcripciones.
      </p>
    </section>
  );
}

// ── actions ─────────────────────────────────────────────────────────────────

function ProfileActions({ profile }: { profile: EditorialSpeakerProfile }) {
  const submit = useSubmitSpeakerCommand();
  const [name, setName] = useState(profile.name);

  const trimmed = name.trim();
  const canRename = trimmed.length >= 2 && trimmed !== profile.name;

  return (
    <section className="voice-actions">
      <form
        className="voice-actions__rename"
        onSubmit={event => {
          event.preventDefault();
          if (!canRename) return;
          submit.mutate({ profileId: profile.profileId, type: 'rename_profile', payload: { name: trimmed } });
        }}
      >
        <label htmlFor="voice-name">Nombre de esta voz</label>
        <input
          id="voice-name"
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder="Ej. Claudia Sheinbaum"
          maxLength={120}
        />
        <Button type="submit" variant="primary" disabled={!canRename} busy={submit.isPending}>
          Guardar nombre
        </Button>
      </form>
      <p className="voice-actions__note">
        Ponerle nombre a una voz la marca como identificada. A partir de la siguiente corrida, sus segmentos aparecen
        con este nombre en las transcripciones.
      </p>

      <MergeControl profile={profile} />

      <div className="voice-actions__row">
        <Button
          onClick={() => submit.mutate({ profileId: profile.profileId, type: 'recompute_centroid' })}
          busy={submit.isPending}
        >
          Recalcular huella
        </Button>
        <Button
          onClick={() => submit.mutate({ profileId: profile.profileId, type: 'archive_profile' })}
          busy={submit.isPending}
          disabled={profile.status === 'archived'}
        >
          Archivar
        </Button>
      </div>
      <p className="voice-actions__note">
        Archivar deja de usar la voz sin borrar nada: sus clips se conservan y se puede reactivar. Todo lo que se hace
        desde aquí es reversible a propósito. Borrar una voz y sus grabaciones de forma definitiva se ejecuta en la
        máquina del pipeline —{' '}
        <span className="mono">pressentia-editorial profiles delete &lt;id&gt;</span> — porque esta API no tiene
        autenticación y un borrado sin vuelta atrás no debe quedar expuesto.
      </p>

      {submit.isError && <p className="voice-actions__error">No se pudo encolar el cambio: {String(submit.error)}</p>}
      {submit.isSuccess && (
        <p className="voice-actions__ok">Cambio encolado. Se aplicará cuando el pipeline procese el siguiente stream.</p>
      )}
    </section>
  );
}

/** Two halves of the same person, discovered on different days, become one identity here. */
function MergeControl({ profile }: { profile: EditorialSpeakerProfile }) {
  const others = useSpeakerProfiles(false);
  const submit = useSubmitSpeakerCommand();
  const [sourceProfileId, setSourceProfileId] = useState('');

  const candidates = useMemo(
    () => (others.data?.profiles ?? []).filter(other => other.profileId !== profile.profileId),
    [others.data, profile.profileId]
  );

  if (candidates.length === 0) return null;

  return (
    <form
      className="voice-actions__merge"
      onSubmit={event => {
        event.preventDefault();
        if (!sourceProfileId) return;
        submit.mutate({ profileId: profile.profileId, type: 'merge_profiles', payload: { sourceProfileId } });
        setSourceProfileId('');
      }}
    >
      <label htmlFor="voice-merge">Fusionar otra voz dentro de esta</label>
      <select id="voice-merge" value={sourceProfileId} onChange={event => setSourceProfileId(event.target.value)}>
        <option value="">Elegir voz…</option>
        {candidates.map(candidate => (
          <option key={candidate.profileId} value={candidate.profileId}>
            {candidate.name} ({candidate.clipCount} clips)
          </option>
        ))}
      </select>
      <Button type="submit" disabled={!sourceProfileId} busy={submit.isPending}>
        Fusionar
      </Button>
      <p className="voice-actions__note">
        Los clips de la voz elegida pasan a esta, y aquella queda archivada — no se borra. Es lo que se hace cuando el
        sistema partió a una misma persona en dos. Si la fusión resulta equivocada, la voz archivada sigue ahí.
      </p>
    </form>
  );
}

// ── clips ───────────────────────────────────────────────────────────────────

function ClipRow({ clip }: { clip: EditorialSpeakerClip }) {
  const submit = useSubmitSpeakerCommand();

  return (
    <article className="clip" data-included={clip.included}>
      <header className="clip__head">
        <span className="clip__when mono">
          {formatClipOffset(clip.startSeconds)} · {clip.durationSeconds.toFixed(1)}s
        </span>
        {clip.streamId && (
          <Link className="clip__stream" to={`/stream/${encodeURIComponent(clip.streamId)}/transcript`}>
            {clip.streamId}
          </Link>
        )}
        {/* The score at capture is the diagnostic: a profile going wrong is usually a run of
            clips that joined near the threshold. */}
        {clip.similarityAtCapture !== null && (
          <span className="clip__similarity mono" title="Parecido con la voz cuando se capturó">
            {clip.similarityAtCapture.toFixed(2)}
          </span>
        )}
        {!clip.included && <Badge tone="unresolved">Excluido</Badge>}
      </header>

      {clip.transcriptText ? (
        <p className="clip__text">“{clip.transcriptText}”</p>
      ) : (
        <p className="clip__text clip__text--absent">Sin transcripción guardada para este clip.</p>
      )}

      {clip.hasAudio ? (
        <ClipAudio clipId={clip.clipId} />
      ) : (
        <p className="clip__text clip__text--absent">
          El audio no se subió. El clip sigue entrenando la voz, pero no se puede escuchar.
        </p>
      )}

      <footer className="clip__actions">
        <Button
          variant="ghost"
          busy={submit.isPending}
          onClick={() =>
            submit.mutate({
              profileId: clip.profileId,
              type: 'set_clip_included',
              payload: { clipId: clip.clipId, included: !clip.included },
            })
          }
        >
          {clip.included ? 'Excluir de la huella' : 'Volver a incluir'}
        </Button>
      </footer>
    </article>
  );
}
